import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Button, Form, InputGroup, ListGroup, Modal, Spinner, Badge, Card } from "react-bootstrap";
import { Search, Plus, Edit2, Trash2, Users, Save, X } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";

import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";
import { useAuth } from "./Home1";

const PHONEBOOK_API = "https://beaconsms.com/oldsite/api/phonebook_api.php";

const PhoneBook = () => {
  const { user } = useAuth();
  const isAdmin = user?.id === "71";
  const userId = user?.id;

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null); // null means "Create New"
  const [formData, setFormData] = useState({ name: "", numbers: "" });

  const fetchGroups = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${PHONEBOOK_API}?user_id=${userId}`);
      setGroups(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching phonebook:", error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    document.title = "Phone Book - beaconSMS";
    fetchGroups();
  }, [fetchGroups]);

  const handleOpenModal = (group = null) => {
    if (group) {
      setEditingGroup(group.id);
      setFormData({ name: group.contact_name, numbers: group.phone_number });
    } else {
      setEditingGroup(null);
      setFormData({ name: "", numbers: "" });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    try {
      const payload = {
        userId,
        name: formData.name,
        phone: formData.numbers,
        action: editingGroup ? "update" : "create",
        groupId: editingGroup
      };
      
      await axios.post(PHONEBOOK_API, payload);
      
      Swal.fire("Success", `Group ${editingGroup ? "updated" : "created"} successfully!`, "success");
      setShowModal(false);
      fetchGroups();
    } catch (error) {
      Swal.fire("Error", "Could not save group.", "error");
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = (groupId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This group will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(PHONEBOOK_API, { userId, groupId, action: "delete" });
          Swal.fire("Deleted!", "Group has been removed.", "success");
          fetchGroups();
        } catch (error) {
          Swal.fire("Error", "Delete failed.", "error");
        }
      }
    });
  };

  const filteredGroups = groups.filter(g => 
    g.contact_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <Header user={user} />
      <Row className='mt-3 px-md-3'>
        <Col md='3' className='mb-3 mb-md-0'>
          <Logout Menu='Login Menu' user={user?.username} />
          <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
          {isAdmin && <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />}
        </Col>

        <Col md='9'>
          <div className="bg-white p-4 border rounded shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
              <div className="d-flex align-items-center">
                <Users className="text-primary me-2" size={24} />
                <h4 className="mb-0 fw-bold text-primary">Phone Book</h4>
              </div>
              <Button variant="primary" size="sm" onClick={() => handleOpenModal()}>
                <Plus size={18} className="me-1" /> New Group
              </Button>
            </div>

            <InputGroup className="mb-4 shadow-sm">
              <InputGroup.Text className="bg-white border-end-0">
                <Search size={18} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                className="border-start-0"
                placeholder="Search groups by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : filteredGroups.length > 0 ? (
              <Row xs={1} md={2} className="g-3">
                {filteredGroups.map(group => (
                  <Col key={group.id}>
                    <Card className="h-100 shadow-sm border-0 bg-light">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="fw-bold mb-1 text-dark">{group.contact_name}</h6>
                            <Badge bg="primary-subtle" className="text-primary border border-primary-subtle">
                              {group.phone_number.split(',').length} Contacts
                            </Badge>
                          </div>
                          <div className="d-flex gap-2">
                            <Button variant="outline-secondary" size="sm" className="p-1" onClick={() => handleOpenModal(group)}>
                              <Edit2 size={14} />
                            </Button>
                            <Button variant="outline-danger" size="sm" className="p-1" onClick={() => handleDelete(group.id)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="text-center py-5 text-muted border rounded bg-light">
                <Users size={48} className="mb-2 opacity-25" />
                <p>No groups found. Create one to get started!</p>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title className="h5 fw-bold">
              {editingGroup ? "Edit Group" : "Create New Group"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">Group Name</Form.Label>
              <Form.Control
                type="text"
                required
                placeholder="e.g. Church Members, Staff, etc."
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">Phone Numbers</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                required
                placeholder="Paste numbers here (separated by commas or new lines)..."
                value={formData.numbers}
                onChange={(e) => setFormData({ ...formData, numbers: e.target.value })}
              />
              <Form.Text className="text-muted">
                System will automatically clean and format these numbers.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={modalLoading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={modalLoading}>
              {modalLoading ? <Spinner size="sm" /> : editingGroup ? "Update Group" : "Save Group"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Footer />
    </Container>
  );
};

export default PhoneBook;